import axios, { AxiosError } from 'axios'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import type {
  ImageInfo,
  Rating,
  RecommendationResponse,
  Token,
  User,
  SimulatedPreferenceProfile,
  UserProgress,
  Phase2Group,
  Phase2Status,
  Phase2StartResponse,
  Phase2RatingCreate,
  Phase2RatingResponse,
} from '../types'

// 创建axios实例
const api = axios.create({
  baseURL: '/api/v1',
  // 推荐接口首次加载可能较慢，适当放宽超时时间
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器：添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器：统一错误处理
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error: AxiosError) => {
    const { response } = error

    // 根据状态码进行不同处理
    if (response) {
      switch (response.status) {
        case 401:
          // 未授权，清除认证信息并跳转到登录页
          useAuthStore.getState().logout()
          toast.error('登录已过期，请重新登录')
          break
        case 403:
          toast.error('没有访问权限')
          break
        case 404:
          toast.error('请求的资源不存在')
          break
        case 500:
          toast.error('服务器内部错误')
          break
        case 422: {
          const detail = (response.data as any)?.detail
          const labels: Record<string, string> = {
            username: '用户名',
            password: '密码',
            age: '年龄',
            gender: '性别',
            jobs: '职业',
            interests: '兴趣',
          }
          const message = Array.isArray(detail)
            ? detail.map((item: any) => {
                const field = item?.loc?.[item.loc.length - 1]
                const label = labels[field] || String(field || '请求')
                return label + ': ' + (item?.msg || '格式不正确')
              }).join('；')
            : String(detail || '提交数据校验失败')
          toast.error(message)
          break
        }
        default:
          toast.error('请求失败: ' + response.status)
      }
    } else {
      toast.error('网络连接失败，请检查网络设置')
    }

    return Promise.reject(error)
  }
)

const get = async <T>(url: string): Promise<T> => api.get(url) as Promise<T>
const post = async <T>(url: string, data?: unknown): Promise<T> => api.post(url, data) as Promise<T>

// API方法封装
export const authAPI = {
  register: (userData: unknown) => post<Token>('/auth/register', userData),
  login: (userData: unknown) => post<Token>('/auth/login', userData),
  getCurrentUser: () => get<User>('/auth/me'),
  getSimulatedPreferences: () => get<SimulatedPreferenceProfile>('/auth/simulated-preferences'),
}

export const ratingAPI = {
  createRating: (ratingData: unknown) => post<Rating[]>('/ratings', ratingData),
  createRatingBatch: (batchData: unknown) => post<Rating[]>('/ratings/batch', batchData),
  getUserRatings: (userId: number) => get<Rating[]>(`/ratings/user/${userId}`),
  getUserProgress: (userId: number) => get<UserProgress>(`/ratings/progress/${userId}`),
}

export const recommendationAPI = {
  getColdStart: (count: number = 6) => get<RecommendationResponse>(`/recommendations/cold-start?count=${count}`),
  getNextBatch: (count: number = 10) => get<RecommendationResponse>(`/recommendations/next-batch?count=${count}`),
  getImageInfo: (imageId: string) => get<ImageInfo>(`/recommendations/images/${imageId}`),
}

export const adminAPI = {
  getUsers: (skip: number = 0, limit: number = 100) => get(`/admin/users?skip=${skip}&limit=${limit}`),
  getRatings: (skip: number = 0, limit: number = 100) => get(`/admin/ratings?skip=${skip}&limit=${limit}`),
  getStats: () => get('/admin/stats'),
  getCollectionOverview: () => get('/admin/collection-overview'),
  getVisualization: () => get('/admin/visualization'),
  exportData: () => get('/admin/export'),
}

export const phase2API = {
  // 查询当前用户第二阶段是否已准备好
  getStatus: () => get<Phase2Status>('/phase2/status'),
  // 启动当前用户的第二阶段生成（后台任务）
  start: () => post<Phase2StartResponse>('/phase2/start'),
  // 获取当前用户的第二阶段图像分组
  getGroups: (batch?: number) =>
    get<Phase2Group[]>(batch !== undefined ? `/phase2/groups?batch=${batch}` : '/phase2/groups'),
  // 提交某一组的排序与评分
  submitRatings: (payload: Phase2RatingCreate) => post<Phase2RatingResponse[]>('/phase2/ratings', payload),
}

export default api
